import React from "react";
import { Archive, Folder } from "lucide-react";
import StageFilterHeader from "../molecules/StageFilterHeader";
import CollaborateurCell from "../molecules/CollaborateurCell";
import EcheanceCell from "../molecules/EcheanceCell";
import SuiviCell from "../molecules/SuiviCell";
import BadgeBloque from "../../BadgeBloque";
import { estBloque } from "../../suiviProdUtils";

const STAGES = [
  { niveau: 1, label: "Numérisation & Dépôt", tone: "info" },
  { niveau: 2, label: "Pré-compta", tone: "warn" },
  { niveau: 3, label: "Révision", tone: "ok" },
];

// Largeurs de colonnes (%) par étape — Échéance et Suivi sont empilés dans
// une seule colonne (voir rendu), l'étape 3 affiche jusqu'à 4 boutons icône
// (demande + clôture mensuelle + notifier + clôturer) contre 2 pour les
// étapes 1/2, donc elle a besoin d'un peu plus de place.
const STAGE_COL_WIDTHS = {
  1: { collaborateur: 8, echeanceSuivi: 15 },
  2: { collaborateur: 8, echeanceSuivi: 15 },
  3: { collaborateur: 8, echeanceSuivi: 17 },
};

function etapeParNiveau(ticket, niveau) {
  return ticket.etapes.find((e) => e.niveau === niveau);
}

function fmt(value) {
  return value
    ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
    : "";
}

function TraitementsComptablesTable({
  tickets,
  users,
  stageFilters,
  onStageFilterChange,
  onOpenCollabMenu,
  onEcheanceChange,
  onNotify,
  onCloture,
  onShowDetails,
  onClientClick,
  sort,
  onToggleSort,
  showArchives,
}) {
  // Affectation et échéance ouvertes à tout collaborateur connecté ; seul le
  // statut de l'étape verrouille (étape précédente non clôturée, ou déjà close).
  const isEditable = (etape) => etape.statut !== "VERROUILLE" && etape.statut !== "CLOTURE";

  const sortIcon = (key) => (sort.key !== key ? "" : sort.dir === "asc" ? " ↑" : " ↓");

  return (
    <div className="pieces-wrap">
      <table className="pieces-table">
        {/* Largeurs fixes en % (table-layout: fixed, voir CSS) : le tableau
            tient toujours dans la largeur de l'écran, sans jamais scroller
            horizontalement — le contenu trop long passe à la ligne ou est
            tronqué (voir CSS) plutôt que de forcer un débordement. */}
        <colgroup>
          <col style={{ width: "9%" }} />
          <col style={{ width: "20%" }} />
          {STAGES.map((stage) => {
            const w = STAGE_COL_WIDTHS[stage.niveau];
            return (
              <React.Fragment key={stage.niveau}>
                <col style={{ width: `${w.collaborateur}%` }} />
                <col style={{ width: `${w.echeanceSuivi}%` }} />
              </React.Fragment>
            );
          })}
        </colgroup>
        <thead>
          <tr className="pieces-grouprow">
            <th
              className={`pc-base pc-sortable ${sort.key === "date" ? "is-sorted" : ""}`}
              rowSpan={3}
              onClick={() => onToggleSort("date")}
            >
              Date d'arrivée{sortIcon("date")}
            </th>
            <th
              className={`pc-base pc-sortable ${sort.key === "client" ? "is-sorted" : ""}`}
              rowSpan={3}
              onClick={() => onToggleSort("client")}
            >
              Client{sortIcon("client")}
            </th>
            {STAGES.map((stage) => (
              <th key={stage.niveau} className={`pc-group tone-${stage.tone}`} colSpan={2}>
                <span className="pc-group-num">{stage.niveau}</span>
                {stage.label}
              </th>
            ))}
          </tr>
          <tr className="pieces-filterrow">
            {STAGES.map((stage) => (
              <th key={stage.niveau} className="pc-filter pc-sub-first" colSpan={2}>
                <StageFilterHeader
                  status={stageFilters[stage.niveau].status}
                  onStatusChange={(value) => onStageFilterChange(stage.niveau, "status", value)}
                  collab={stageFilters[stage.niveau].collab}
                  onCollabChange={(value) => onStageFilterChange(stage.niveau, "collab", value)}
                  users={users}
                />
              </th>
            ))}
          </tr>
          <tr className="pieces-subrow">
            {STAGES.map((stage) => (
              <React.Fragment key={stage.niveau}>
                <th className="pc-sub pc-sub-first">Collaborateur</th>
                <th className="pc-sub pc-sub-center">Échéance & Suivi</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr key={ticket.id} className={estBloque(ticket.client) ? "ligne-bloquee" : undefined}>
              <td className="pieces-date">{fmt(ticket.dateArrivee) || "—"}</td>
              <td className="pieces-client">
                <span className="pieces-client-name" onClick={() => onClientClick(ticket.client)}>
                  {ticket.client?.nom || "—"}
                </span>
                {estBloque(ticket.client) && <BadgeBloque />}
                {ticket.naturePieces && (
                  <span className="pieces-client-nature" title={ticket.naturePieces}>
                    {ticket.naturePieces}
                  </span>
                )}
                {ticket.periode && <span className="pieces-client-periode">{ticket.periode}</span>}
              </td>
              {STAGES.map((stage) => {
                const etape = etapeParNiveau(ticket, stage.niveau);
                if (!etape) {
                  return (
                    <React.Fragment key={stage.niveau}>
                      <td className="pc-cell pc-cell-first"></td>
                      <td className="pc-cell pc-cell-last"></td>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={stage.niveau}>
                    <td className="pc-cell pc-cell-first">
                      <CollaborateurCell
                        assignee={etape.assignee}
                        canEdit={isEditable(etape)}
                        onToggle={(rect) =>
                          onOpenCollabMenu(rect, ticket, etape.niveau, etape.assignee)
                        }
                      />
                    </td>
                    <td className="pc-cell pc-cell-last">
                      <div className="pc-echeance-suivi">
                        {etape.statut !== "CLOTURE" && (
                          <EcheanceCell
                            dueDate={etape.dueDate}
                            overdue={etape.enRetardLive}
                            canEdit={isEditable(etape)}
                            onChange={(date) => onEcheanceChange(ticket.id, etape.niveau, date)}
                          />
                        )}
                        <SuiviCell
                          etape={etape}
                          onNotify={() => onNotify(ticket, etape)}
                          onCloture={() => onCloture(ticket, etape)}
                          onShowDetails={() => onShowDetails(ticket, etape)}
                        />
                      </div>
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
          {tickets.length === 0 && (
            <tr className="pieces-empty-row">
              <td colSpan={8}>
                <div className="pieces-empty">
                  {showArchives ? <Archive size={26} /> : <Folder size={26} />}
                  <div>{showArchives ? "Aucun dossier archivé" : "Aucune arrivée de pièces"}</div>
                  <small>
                    {showArchives
                      ? "Les dossiers dont les 3 étapes sont clôturées apparaîtront ici."
                      : "Créez un évènement de catégorie « Arrivée pièces comptables » pour alimenter cet écran."}
                  </small>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TraitementsComptablesTable;
